import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Flame, CreditCard, Coins, ExternalLink } from 'lucide-react';
import { DirectBuyEthPanel } from './DirectBuyEthPanel';
import { MetaMaskBuyPanel } from './MetaMaskBuyPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pas-eth`;

const History = ({ rows }) => (
  <div className="mt-5 space-y-2" data-testid="eth-purchase-history">
    {rows.map(p => (
      <div key={p.purchase_id} className="flex items-center justify-between text-xs bg-slate-900/60 rounded-lg px-3 py-2" data-testid={`eth-purchase-row-${p.purchase_id}`}>
        <span className="text-slate-300">{p.eth_amount} ETH · ${Number(p.usd_amount).toFixed(2)} · {p.payment_method === 'beth' ? 'BETH' : 'Card'}</span>
        <span className="flex items-center gap-2">
          <span className={p.status === 'completed' ? 'text-emerald-400' : 'text-amber-300'}>{p.status.replace(/_/g, ' ')}</span>
          {p.explorer_url && <a href={p.explorer_url} target="_blank" rel="noreferrer" className="text-purple-400"><ExternalLink className="w-3.5 h-3.5" /></a>}
        </span>
      </div>
    ))}
  </div>
);

export const BuyEthCard = ({ data, token, onChange }) => {
  const [usd, setUsd] = useState('50');
  const [dest, setDest] = useState('custodial');
  const [quote, setQuote] = useState(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const loadRows = () => axios.get(`${API}/purchases`, h).then(r => setRows(r.data.purchases)).catch(() => {});
  useEffect(() => { loadRows(); }, [token]);
  useEffect(() => {
    const amt = Number(usd);
    if (!amt) { setQuote(null); return; }
    const t = setTimeout(() => axios.get(`${API}/quote?usd_amount=${amt}`, h)
      .then(r => setQuote(r.data)).catch(e => setQuote({ error: e.response?.data?.detail || 'Quote unavailable' })), 400);
    return () => clearTimeout(t);
  }, [usd, token]);
  const body = { usd_amount: Number(usd), destination: dest };
  const payCard = async () => {
    setBusy(true);
    try { const r = await axios.post(`${API}/checkout`, body, h); window.location.href = r.data.checkout_url; }
    catch (e) { toast.error(e.response?.data?.detail || 'Checkout failed'); setBusy(false); }
  };
  const payBeth = async () => {
    setBusy(true);
    try { const r = await axios.post(`${API}/buy-with-beth`, body, h); toast.success(r.data.message); loadRows(); onChange?.(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Purchase failed'); }
    finally { setBusy(false); }
  };
  const short = quote?.available_eth != null && quote.eth_amount > quote.available_eth;
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-6" data-testid="buy-eth-card">
      <div className="flex items-center gap-3 mb-1"><Flame className="w-5 h-5 text-sky-400" /><h2 className="text-white font-semibold">Buy Ethereum for gas</h2></div>
      <p className="text-xs text-slate-400 mb-4">Real ETH on Ethereum mainnet, delivered to your BETH wallet (or linked wallet). Pick an amount, then choose how to pay.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="text-xs text-slate-400">Amount (USD)
          <input type="number" min="10" max="5000" value={usd} onChange={e => setUsd(e.target.value)} data-testid="buy-eth-usd-input"
            className="mt-1 w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white" />
        </label>
        <label className="text-xs text-slate-400">Send to
          <select value={dest} onChange={e => setDest(e.target.value)} data-testid="buy-eth-destination-select"
            className="mt-1 w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white">
            <option value="custodial">My BETH wallet ({data.wallet_address?.slice(0, 8)}…)</option>
            <option value="linked" disabled={!data.linked_wallet}>{data.linked_wallet?.label || 'Linked'} wallet {data.linked_wallet ? `(${data.linked_wallet.address.slice(0, 8)}…)` : '(none linked)'}</option>
          </select>
        </label>
      </div>
      {/* Direct Buy - Primary Option (No API keys needed) */}
      <div className="mt-4">
        <DirectBuyEthPanel 
          usd={usd} 
          walletAddress={dest === 'linked' && data.linked_wallet ? data.linked_wallet.address : data.wallet_address}
          walletLabel={dest === 'linked' ? 'Linked' : 'BETH'}
        />
      </div>
      <p className="text-sm text-slate-300 font-semibold mt-5" data-testid="reserve-buy-heading">Or buy from the Treasury Reserve Wallet</p>
      <p className="text-xs text-slate-500">Live market price plus a {quote?.fee_pct ?? 2}% fee; sent from the BETHCOIN Treasury Reserve Wallet.</p>
      <div className="mt-3 bg-slate-900/60 rounded-lg p-3 text-sm" data-testid="buy-eth-quote">
        {!quote && <p className="text-slate-500">Enter an amount between $10 and $5,000</p>}
        {quote?.error && <p className="text-red-400" data-testid="buy-eth-quote-error">{quote.error}</p>}
        {quote && !quote.error && (<>
          <p className="text-white font-semibold" data-testid="buy-eth-quote-eth">You receive {quote.eth_amount} ETH</p>
          <p className="text-xs text-slate-400">1 ETH = ${quote.eth_price_usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ({quote.price_source}) · fee ${quote.fee_usd.toFixed(2)} · or {quote.beth_cost.toLocaleString()} BETH from your balance</p>
          {short && <p className="text-xs text-amber-300 mt-1" data-testid="buy-eth-unavailable">Treasury Reserve Wallet has only {quote.available_eth} ETH available right now; reserve purchases are paused until it is funded. Use "Buy ETH by card with Stripe" above meanwhile.</p>}
        </>)}
      </div>
      <div className="flex flex-wrap gap-3 mt-4">
        <button onClick={payCard} disabled={busy || !quote || quote.error || short} data-testid="buy-eth-card-btn"
          className="px-4 py-2 bg-sky-500 hover:bg-sky-600 disabled:opacity-40 text-white rounded-lg text-sm flex items-center gap-2"><CreditCard className="w-4 h-4" /> Pay by card</button>
        <button onClick={payBeth} disabled={busy || !quote || quote.error || short || data.beth_balance < (quote?.beth_cost || 0)} data-testid="buy-eth-beth-btn"
          className="px-4 py-2 border border-emerald-500 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-40 rounded-lg text-sm flex items-center gap-2"><Coins className="w-4 h-4" /> Pay with BETH balance</button>
      </div>
      <MetaMaskBuyPanel usd={Number(usd) || 50} linked={data.linked_wallet} token={token} onLinked={onChange} />
      {rows.length > 0 && <History rows={rows} />}
    </div>
  );
};
